/**
 * AI 输出里的实体链接（`[文字](<type>:<id>)`，借用 markdown 链接语法；type 与后端 EntityRef.type 同一套，
 * 设计 docs/superpowers/specs/2026-10-07-assistant-structured-output-design.md §2）。
 * 注册表是代码、显式绑定，不按 ID 猜类型：
 * - task / intent / reservation → 当前页开 flyout（ENTITY_SCHEMES）；
 * - review / listing / purchase / conversation / knowledge_article / user / owner / location / asset / group / ticket
 *   → 当前标签页跳到它的页面（ENTITY_PAGES；后五个随全局搜索加入，助手回答里的这几类链接也一起能点开）。
 * 渲染前把已注册的链接改写成内部锚点 `#nova-entity/<type>/<id>`（由点击处理打开）；
 * 未注册的 type（含 javascript: 等）只保留文字；http(s) / mailto 链接原样交给 markdown 渲染（DOMPurify 兜底）。
 * 路由由前端自己维护，不用后端的 web_path（那是给 MCP 等外部调用方的）。
 */
export const ENTITY_SCHEMES = ['task', 'intent', 'reservation'] as const
export type EntityScheme = (typeof ENTITY_SCHEMES)[number]

/** 没有 flyout、但有自己页面的实体：点了在当前标签页跳过去 */
export const ENTITY_PAGES: Readonly<Record<string, (id: string) => string>> = {
  review: (id) => `/reviews/${id}`,
  listing: (id) => `/listings/${id}`,
  purchase: (id) => `/procurement/${id}`,
  conversation: (id) => `/inbox?conversationId=${id}`,
  knowledge_article: (id) => `/knowledge-base/${id}`,
  user: (id) => `/users/${id}`,
  owner: (id) => `/owners/${id}`,
  asset: (id) => `/asset-mgr/assets?assetId=${id}`,
  // 分组没有给运营看的详情页：搜到分组 = 看这个分组（含子分组）的房子（全局搜索方案 Q5，Frank 2026-10-23）
  group: (id) => `/listings?propertyGroup=${id}`,
  // 诉求：去 Inbox 的 To do 并打开这件的详情（同后端 web_path，tickets facade §17）
  ticket: (id) => `/inbox?mode=todo&ticketId=${id}`,
}

export function entityPagePath(type: string, id: string): string | null {
  return Object.prototype.hasOwnProperty.call(ENTITY_PAGES, type) ? ENTITY_PAGES[type](encodeURIComponent(id)) : null
}

/** 点得开的实体类型：有 flyout，或有自己的页面 */
export function isLinkableEntity(type: string): boolean {
  return isEntityScheme(type) || entityPagePath(type, 'x') !== null
}

const ANCHOR_PREFIX = '#nova-entity/'
const PASS_THROUGH = /^(https?:|mailto:|#)/i
// [文字](scheme:target)：scheme 为字母开头的 URI scheme（类型名可带下划线，如 knowledge_article）；文字不跨行、不含方括号；
// target 允许一层成对括号（如 javascript:alert(1)），整段都被识别，不留残渣
const LINK = /\[([^\]\n]+)\]\(([a-zA-Z][a-zA-Z0-9_+.-]*):([^()\s]*(?:\([^()\s]*\)[^()\s]*)*)\)/g

export function isEntityScheme(s: string): s is EntityScheme {
  return (ENTITY_SCHEMES as readonly string[]).includes(s)
}

export function rewriteEntityLinks(md: string): string {
  return md.replace(LINK, (whole, text: string, scheme: string, target: string) => {
    if (PASS_THROUGH.test(`${scheme}:`)) return whole
    if (isLinkableEntity(scheme.toLowerCase()) && /^[A-Za-z0-9_-]+$/.test(target)) return `[${text}](${ANCHOR_PREFIX}${scheme.toLowerCase()}/${target})`
    return text
  })
}

export function parseEntityHref(href: string | null | undefined): { scheme: string; id: string } | null {
  if (!href?.startsWith(ANCHOR_PREFIX)) return null
  const [scheme, id] = href.slice(ANCHOR_PREFIX.length).split('/')
  return scheme && id && isLinkableEntity(scheme) ? { scheme, id } : null
}

/**
 * 助手回答里的内部深链（后端给模型的 `web_path`，如 `/tasks/123`、`/intents?intentId=45`）→ 对应的 flyout。
 * 只认本站：相对路径，或同源的绝对地址；别的站、别的路径返回 null（照常新开标签）。
 */
export function parseInAppEntityPath(href: string | null | undefined, origin: string): { scheme: EntityScheme | 'ticket'; id: string } | null {
  if (!href) return null
  let url: URL
  try { url = new URL(href, origin) } catch { return null }
  if (url.origin !== origin) return null
  const task = /^\/tasks\/(\d+)\/?$/.exec(url.pathname)
  if (task) return { scheme: 'task', id: task[1] }
  // 预订：`/reservations?reservationId=<id>`（后端给模型的 web_path）与整页详情 `/reservations/<id>`
  const reservationPath = /^\/reservations\/([A-Za-z0-9_-]+)\/?$/.exec(url.pathname)
  if (reservationPath) return { scheme: 'reservation', id: reservationPath[1] }
  const reservationId = url.searchParams.get('reservationId')
  if (/^\/reservations\/?$/.test(url.pathname) && reservationId && /^[A-Za-z0-9_-]+$/.test(reservationId)) return { scheme: 'reservation', id: reservationId }
  const intentId = url.searchParams.get('intentId')
  if (/^\/intents\/?$/.test(url.pathname) && intentId && /^[A-Za-z0-9_-]+$/.test(intentId)) return { scheme: 'intent', id: intentId }
  // 诉求：`/inbox?mode=todo&ticketId=<id>`（get_ticket 的 web_path）→ 跳过去（ENTITY_PAGES.ticket）
  const ticketId = url.searchParams.get('ticketId')
  if (/^\/inbox\/?$/.test(url.pathname) && url.searchParams.get('mode') === 'todo' && ticketId && /^[A-Za-z0-9_-]+$/.test(ticketId)) return { scheme: 'ticket', id: ticketId }
  return null
}
